import React, { useState } from "react"
import { FaWhatsapp } from "react-icons/fa"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import {
  WhatsAppConversationThreadPreview,
  type ConversationTurn,
} from "../communication/MessagePreviewViews"

const WhatsAppIcon = FaWhatsapp as React.ElementType

export type ApproachWebhookEvent = Record<string, unknown>

export type ApproachWhatsAppMessage = {
  role: "outbound" | "user" | "model"
  text: string
  sentAt?: string
}

export type ApproachWhatsAppChat = {
  messages?: ApproachWhatsAppMessage[]
  outcome?: "in_progress" | "not_interested" | "interested" | "not_qualified"
  outcomeReason?: string
  outcomeEvaluatedAt?: string
}

const OUTCOME_LABELS: Record<
  NonNullable<ApproachWhatsAppChat["outcome"]>,
  string
> = {
  in_progress: "In progress",
  not_interested: "Not interested",
  interested: "Interested",
  not_qualified: "Not qualified",
}

const OUTCOME_BADGE_CLASS: Record<
  NonNullable<ApproachWhatsAppChat["outcome"]>,
  string
> = {
  in_progress: "bg-amber-100 text-amber-800",
  not_interested: "bg-slate-100 text-slate-700",
  interested: "bg-emerald-100 text-emerald-800",
  not_qualified: "bg-rose-100 text-rose-800",
}

export const WHATSAPP_OUTCOME_FILTER_OPTIONS = [
  { value: "interested", label: OUTCOME_LABELS.interested },
  { value: "not_interested", label: OUTCOME_LABELS.not_interested },
  { value: "not_qualified", label: OUTCOME_LABELS.not_qualified },
  { value: "in_progress", label: OUTCOME_LABELS.in_progress },
  { value: "none", label: "No WhatsApp outcome" },
] as const

export const OUTCOME_TEXT_CLASS: Record<
  NonNullable<ApproachWhatsAppChat["outcome"]>,
  string
> = {
  in_progress: "text-amber-700",
  not_interested: "text-slate-600",
  interested: "text-emerald-700",
  not_qualified: "text-rose-700",
}

function buildTemplateFromContext(context: Record<string, unknown>): string {
  const name = String(context.name ?? context.candidate_first_name ?? "").trim()
  const role = String(
    context.role ?? context.job_title ?? context.title ?? ""
  ).trim()
  const company = String(
    context.company ?? context.company_name ?? ""
  ).trim()
  const location = String(
    context.location ?? context.job_location ?? ""
  ).trim()

  if (!name && !role && !company) return ""

  const lines = [`Hi ${name || "there"},`]
  if (role || company || location) {
    lines.push(
      `We're currently hiring for a ${role || "role"} role${company ? ` at ${company}` : ""}${location ? ` in ${location}` : ""}, and your profile looks like a strong match.`
    )
  }
  lines.push("Are you interested in this opportunity?")
  return lines.join("\n\n")
}

function parseTurnTime(raw: unknown): number {
  if (raw == null || raw === "") return 0;
  const t = new Date(String(raw)).getTime();
  return Number.isFinite(t) ? t : 0;
}

const ROLE_SORT_ORDER: Record<ConversationTurn["role"], number> = {
  outbound: 0,
  user: 1,
  model: 2,
}

function readStringField(
  body: Record<string, unknown>,
  meta: Record<string, unknown>,
  keys: string[]
): string {
  for (const key of keys) {
    const top = body[key]
    if (typeof top === "string" && top.trim()) return top.trim()
    const nested = meta[key]
    if (typeof nested === "string" && nested.trim()) return nested.trim()
  }
  return ""
}

function sortTurns(turns: ConversationTurn[]): ConversationTurn[] {
  return [...turns].sort((a, b) => {
    const ta = parseTurnTime(a.timeIso)
    const tb = parseTurnTime(b.timeIso)
    if (ta !== tb) return ta - tb
    return ROLE_SORT_ORDER[a.role] - ROLE_SORT_ORDER[b.role]
  })
}

function mergeConversationTurns(...groups: ConversationTurn[][]): ConversationTurn[] {
  const merged: ConversationTurn[] = []

  for (const group of groups) {
    for (const turn of group) {
      const normalized = turn.text.trim()
      if (!normalized) continue

      const turnTime = parseTurnTime(turn.timeIso)
      const duplicate = merged.some((item) => {
        if (item.role !== turn.role || item.text.trim() !== normalized) return false
        const itemTime = parseTurnTime(item.timeIso)
        if (turnTime > 0 && itemTime > 0) {
          return Math.abs(turnTime - itemTime) < 5000
        }
        return turn.timeIso === item.timeIso
      })
      if (duplicate) continue

      merged.push({ ...turn, text: normalized })
    }
  }

  return sortTurns(merged)
}

function mergeConversationTurnsPreservingPrimary(
  primary: ConversationTurn[],
  secondary: ConversationTurn[]
): ConversationTurn[] {
  const merged = [...primary]

  for (const turn of secondary) {
    const normalized = turn.text.trim()
    if (!normalized) continue

    const turnTime = parseTurnTime(turn.timeIso)
    const duplicate = merged.some((item) => {
      if (item.role !== turn.role || item.text.trim() !== normalized) return false
      const itemTime = parseTurnTime(item.timeIso)
      if (turnTime > 0 && itemTime > 0) {
        return Math.abs(turnTime - itemTime) < 5000
      }
      return turn.timeIso === item.timeIso
    })
    if (duplicate) continue

    merged.push({ ...turn, text: normalized })
  }

  return merged
}

function pushTurn(
  turns: ConversationTurn[],
  role: ConversationTurn["role"],
  text: string,
  timeIso: string,
  sequence: { value: number }
) {
  const normalized = text.trim()
  if (!normalized) return
  const base = parseTurnTime(timeIso)
  const orderedTime =
    base > 0
      ? new Date(base + sequence.value++).toISOString()
      : timeIso
  turns.push({ role, text: normalized, timeIso: orderedTime })
}

function extractTurnsFromLogs(
  ev: ApproachWebhookEvent,
  meta: Record<string, unknown>
): ConversationTurn[] {
  const logs = ev.logs ?? meta.logs
  if (!Array.isArray(logs)) return []

  const turns: ConversationTurn[] = []
  for (const entry of logs) {
    if (!entry || typeof entry !== "object") continue
    const row = entry as Record<string, unknown>
    const role = String(row.conversationRole ?? row.role ?? "").trim()
    const text = String(row.text ?? row.message ?? row.body ?? "").trim()
    if (
      (role === "outbound" || role === "user" || role === "model") &&
      text
    ) {
      turns.push({
        role: role as ConversationTurn["role"],
        text,
        timeIso: String(row.timestamp ?? row.sentAt ?? ""),
      })
    }
  }
  return turns
}

function extractTurnsFromWebhookFields(
  ev: ApproachWebhookEvent
): ConversationTurn[] {
  const event = String(ev.event ?? "").toLowerCase()
  const timeIso = String(ev.timestamp ?? ev.receivedAt ?? "")
  const meta =
    ev.meta && typeof ev.meta === "object"
      ? (ev.meta as Record<string, unknown>)
      : {}
  const context =
    ev.context && typeof ev.context === "object"
      ? (ev.context as Record<string, unknown>)
      : {}

  const inboundText = readStringField(ev, meta, [
    "inboundText",
    "userMessage",
    "userText",
    "candidateMessage",
    "incomingText",
  ])
  const replyText = readStringField(ev, meta, [
    "replyText",
    "assistantReply",
    "assistantText",
    "responseText",
    "modelReply",
    "generatedReply",
    "aiReply",
  ])
  const outboundText = readStringField(ev, meta, [
    "body",
    "initialTemplateBody",
    "message",
    "text",
    "templateBody",
    "renderedBody",
  ])

  const turns: ConversationTurn[] = []
  const sequence = { value: 0 }
  const isInitial = ["initial_message_sent", "conversation_started"].includes(event)

  if (isInitial) {
    const text = outboundText || buildTemplateFromContext(context)
    if (text) pushTurn(turns, "outbound", text, timeIso, sequence)
  } else if (outboundText && !inboundText && !replyText) {
    pushTurn(turns, isInitial ? "outbound" : "model", outboundText, timeIso, sequence)
  }

  if (inboundText) pushTurn(turns, "user", inboundText, timeIso, sequence)
  if (replyText) pushTurn(turns, "model", replyText, timeIso, sequence)

  if (
    !replyText &&
    outboundText &&
    inboundText &&
    (event === "conversation_turn" || event.includes("reply"))
  ) {
    pushTurn(turns, "model", outboundText, timeIso, sequence)
  }

  return sortTurns(turns)
}

export function extractTurnsFromWebhookEvents(
  events: ApproachWebhookEvent[] | undefined
): ConversationTurn[] {
  if (!Array.isArray(events) || events.length === 0) return []

  const sorted = [...events].sort((a, b) => {
    const ta = parseTurnTime(a.timestamp ?? a.receivedAt)
    const tb = parseTurnTime(b.timestamp ?? b.receivedAt)
    return ta - tb
  })

  for (let i = sorted.length - 1; i >= 0; i--) {
    const ev = sorted[i]
    const meta =
      ev.meta && typeof ev.meta === "object"
        ? (ev.meta as Record<string, unknown>)
        : {}
    const fromLogs = extractTurnsFromLogs(ev, meta)
    if (fromLogs.length > 0) return fromLogs
  }

  const turns: ConversationTurn[] = []
  for (const ev of sorted) {
    turns.push(...extractTurnsFromWebhookFields(ev))
  }

  return mergeConversationTurns(turns)
}

export function extractTurnsFromWhatsAppChat(
  chat: ApproachWhatsAppChat | undefined
): ConversationTurn[] {
  if (!chat?.messages?.length) return []

  return sortTurns(
    chat.messages.map((message) => ({
      role: message.role,
      text: message.text,
      timeIso: message.sentAt ? String(message.sentAt) : "",
    }))
  )
}

export function extractApproachConversationTurns(args: {
  whatsappChat?: ApproachWhatsAppChat
  webhookEvents?: ApproachWebhookEvent[]
}): ConversationTurn[] {
  const fromChat = extractTurnsFromWhatsAppChat(args.whatsappChat)
  const fromEvents = extractTurnsFromWebhookEvents(args.webhookEvents)

  if (fromEvents.length === 0) return fromChat
  if (fromChat.length === 0) return fromEvents

  return mergeConversationTurnsPreservingPrimary(fromEvents, fromChat)
}

type ApproachWhatsAppChatPopoverProps = {
  webhookEvents?: ApproachWebhookEvent[]
  whatsappChat?: ApproachWhatsAppChat
  candidateName?: string
  phone?: string
  status?: string
  /** Funnel table: show only the green WhatsApp icon (no outcome label). */
  iconOnly?: boolean
  /** Load the thread when the icon is opened instead of with the table. */
  loadConversation?: () => Promise<{
    whatsappChat?: ApproachWhatsAppChat | null
    webhookEvents?: ApproachWebhookEvent[] | null
  }>
}

const WHATSAPP_TRIGGER_STATUSES = new Set([
  "Ringing Not Reachable",
  "Not Reachable",
])

export default function ApproachWhatsAppChatPopover({
  webhookEvents,
  whatsappChat,
  candidateName,
  phone,
  status,
  iconOnly = false,
  loadConversation,
}: ApproachWhatsAppChatPopoverProps) {
  const [open, setOpen] = useState(false)
  const [loadingChat, setLoadingChat] = useState(false)
  const [fetched, setFetched] = useState<{
    whatsappChat?: ApproachWhatsAppChat | null
    webhookEvents?: ApproachWebhookEvent[] | null
  } | null>(null)

  const activeChat = fetched?.whatsappChat ?? whatsappChat
  const activeEvents = fetched?.webhookEvents ?? webhookEvents
  const turns = extractApproachConversationTurns({
    whatsappChat: activeChat || undefined,
    webhookEvents: activeEvents || undefined,
  })
  const hasEvents = Array.isArray(activeEvents) && activeEvents.length > 0
  const hasChat = Boolean(activeChat?.messages?.length)
  const isTriggerStatus = status ? WHATSAPP_TRIGGER_STATUSES.has(status) : false

  if (!hasEvents && !hasChat && !isTriggerStatus && !loadConversation) return null

  const title = candidateName || phone || "WhatsApp"
  const outcome = activeChat?.outcome ?? "in_progress"

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next || !loadConversation || fetched || loadingChat) return
    setLoadingChat(true)
    loadConversation()
      .then((data) => setFetched(data))
      .catch(() => setFetched({ whatsappChat: null, webhookEvents: [] }))
      .finally(() => setLoadingChat(false))
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`View WhatsApp messages — ${OUTCOME_LABELS[outcome]}`}
          title={activeChat?.outcomeReason || OUTCOME_LABELS[outcome]}
          className={`inline-flex items-center gap-1 text-[calc(1rem-5px)] font-semibold leading-none whitespace-nowrap transition hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-[#25D366]/40 rounded-sm ${
            iconOnly ? "p-0.5" : ""
          }`}
        >
          <WhatsAppIcon className={`${iconOnly ? "h-4 w-4" : "h-3.5 w-3.5"} shrink-0 text-[#25D366]`} />
          {!iconOnly ? (
            <span className={OUTCOME_TEXT_CLASS[outcome]}>{OUTCOME_LABELS[outcome]}</span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="end"
        sideOffset={8}
        className="w-auto max-w-[min(22rem,calc(100vw-2rem))] border-0 bg-transparent p-2 shadow-none"
      >
        {loadingChat ? (
          <div className="overflow-hidden rounded-md border border-black/10 shadow-md">
            <div className="bg-white px-3 py-4 text-sm text-muted-foreground">Loading messages...</div>
          </div>
        ) : turns.length > 0 ? (
          <div className="space-y-2">
            <div className="overflow-hidden rounded-md border border-black/10 bg-white px-3 py-2 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${OUTCOME_BADGE_CLASS[outcome]}`}
                >
                  {OUTCOME_LABELS[outcome]}
                </span>
                {activeChat?.outcomeReason ? (
                  <span className="text-[11px] text-muted-foreground">
                    {activeChat.outcomeReason}
                  </span>
                ) : null}
              </div>
            </div>
            <WhatsAppConversationThreadPreview turns={turns} />
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-black/10 shadow-md">
            <div className="flex items-center gap-2 bg-[#008069] px-3 py-2.5 text-white">
              <span className="text-[13px] font-semibold tracking-tight">WhatsApp</span>
              <span className="text-[10px] font-normal opacity-80">{title}</span>
            </div>
            <div className="bg-white px-3 py-4 text-sm text-muted-foreground">
              No WhatsApp messages yet for this approach.
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
